import {Link as RouterLink} from 'react-router-dom';
import {Button, Link} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {
  REFERRAL_PAYMENT_STATUS_LABELS,
  REFERRAL_TARGET_ACTION_LABELS,
  ReferralRecord,
  useGetReferrals,
} from '@entities/referral';
import {MrTable, TableRowActionsContainer} from '@shared/components';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__USER_ACCOUNT,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';

import {PaginationState} from '@/shared/types';

const localStorageKey = 'referralsTable';

type UrlState = PaginationState;

const formatMoney = (value: number): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const columns: MRT_ColumnDef<ReferralRecord>[] = [
  {
    accessorKey: 'masterAccountId',
    header: 'Master Account ID',
    size: 170,
    Cell: ({row}) => {
      const {masterAccountId} = row.original;

      return (
        <Link
          component={RouterLink}
          to={ROUTE__USER_ACCOUNT.replace(':id', masterAccountId)}
        >
          {masterAccountId}
        </Link>
      );
    },
  },
  {
    accessorKey: 'referralAccountId',
    header: 'Referral Account ID',
    size: 170,
    Cell: ({row}) => {
      const {referralAccountId} = row.original;

      return (
        <Link
          component={RouterLink}
          to={ROUTE__USER_ACCOUNT.replace(':id', referralAccountId)}
        >
          {referralAccountId}
        </Link>
      );
    },
  },
  {
    accessorKey: 'registeredAt',
    header: 'Registration date and time',
    size: 190,
    Cell: ({row}) => formatInTz(row.original.registeredAt, DATETIME_FORMAT),
  },
  {
    accessorKey: 'firstRealOrderAt',
    header: 'First real order date and time',
    size: 210,
    Cell: ({row}) =>
      row.original.firstRealOrderAt
        ? formatInTz(row.original.firstRealOrderAt, DATETIME_FORMAT)
        : 'N/A',
  },
  {
    accessorKey: 'targetAction',
    header: 'Target action',
    size: 150,
    Cell: ({row}) => REFERRAL_TARGET_ACTION_LABELS[row.original.targetAction],
  },
  {
    accessorKey: 'paymentAmount',
    header: 'Payment amount',
    size: 140,
    Cell: ({row}) => formatMoney(row.original.paymentAmount),
  },
  {
    accessorKey: 'paymentStatus',
    header: 'Payment status',
    size: 140,
    Cell: ({row}) => REFERRAL_PAYMENT_STATUS_LABELS[row.original.paymentStatus],
  },
  {
    accessorKey: 'referralPnl',
    header: "Referral's P&L",
    size: 140,
    Cell: ({row}) => formatMoney(row.original.referralPnl),
  },
];

export const ReferralsPage = () => {
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {
    data,
    isPending: isLoading,
    error,
  } = useGetReferrals({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderTopToolbarCustomActions={() => {
        return <Button>Add referrals</Button>;
      }}
      renderRowActions={() => {
        return (
          <TableRowActionsContainer>
            <Button variant="outlined" color="error">
              Block
            </Button>
          </TableRowActionsContainer>
        );
      }}
      enableColumnOrdering
      enableColumnDragging={false}
      enableColumnPinning
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      enablePagination
      manualPagination
      onPaginationChange={setUrl}
      rowCount={data?.total ?? 0}
      state={{
        isLoading,
        pagination: urlState,
      }}
      initialState={{
        columnPinning: {
          left: ['masterAccountId'],
          right: ['mrt-row-actions'],
        },
      }}
      {...entityTableCommonProps}
    />
  );
};
